import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Shared staff theme follows the device until the user explicitly selects a mode.
 * Safe for SSR (guards window/document usage) and memoizes the value object to avoid needless re-renders.
 */

interface EmployeeThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
}

const EmployeeThemeContext = createContext<EmployeeThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'aa_employee_theme';
// The old key was written on every mount, even for an automatic system choice.
// Only this explicit override can distinguish a user's click from that legacy value.
const OVERRIDE_KEY = 'aa_crm_theme_override';

export const EmployeeThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const parent = useContext(EmployeeThemeContext);
  return parent ? <>{children}</> : <EmployeeThemeRoot>{children}</EmployeeThemeRoot>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalEmployeeTheme = () => useContext(EmployeeThemeContext);

const EmployeeThemeRoot: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [override, setOverride] = useState<'dark' | 'light' | null>(() => {
    try {
      const stored = window.localStorage.getItem(OVERRIDE_KEY);
      return stored === 'dark' || stored === 'light' ? stored : null;
    } catch { return null; }
  });
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false; // SSR fallback - default to light
    try {
      const stored = window.localStorage.getItem(OVERRIDE_KEY);
      if (stored === 'dark') return true;
      if (stored === 'light') return false;
      // No stored preference; respect system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return true;
      }
    } catch { /* ignore */ }
    return false; // default light for employees
  });

  useEffect(() => {
    if (override) { setIsDark(override === 'dark'); return; }
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return;
    const update = () => setIsDark(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [override]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.classList.toggle('employee-dark-mode', isDark);
      document.body.classList.toggle('employee-light-mode', !isDark);
    }
    return () => { document.body.classList.remove('employee-dark-mode', 'employee-light-mode'); };
  }, [isDark]);

  const toggleTheme = useCallback(() => {
    const next = isDark ? 'light' : 'dark';
    setOverride(next);
    setIsDark(next === 'dark');
    try {
      window.localStorage.setItem(OVERRIDE_KEY, next);
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch { /* In-memory switching remains available. */ }
  }, [isDark]);

  const value = useMemo<EmployeeThemeContextType>(() => ({ isDark, toggleTheme }), [isDark, toggleTheme]);

  return <EmployeeThemeContext.Provider value={value}>{children}</EmployeeThemeContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useEmployeeTheme = (): EmployeeThemeContextType => {
  const ctx = useContext(EmployeeThemeContext);
  if (!ctx) throw new Error('useEmployeeTheme must be used within an EmployeeThemeProvider');
  return ctx;
};
